import { useQuery } from "@tanstack/react-query";
import type { OnlineRes, ServerStatus, SiteContent, SiteContentList } from "@etheria/shared";
import { api } from "./api";

/**
 * Hooks de données publiques partagés (Header, Accueil, Statut).
 * Sources : BFF `/api/public/online` (WebAPI R1), `/api/public/server` (R20/R21),
 * `/api/content` (CMS éditorial — hero/annonces/pages publiés).
 */

export function useOnline() {
  return useQuery({
    queryKey: ["public", "online"],
    queryFn: ({ signal }) => api.get<OnlineRes>("/public/online", { signal }),
    refetchInterval: 20_000,
    staleTime: 15_000,
  });
}

export function useServerStatus() {
  return useQuery({
    queryKey: ["public", "server"],
    queryFn: ({ signal }) => api.get<ServerStatus>("/public/server", { signal }),
    refetchInterval: 30_000,
    staleTime: 20_000,
  });
}

/**
 * Contenu éditorial PUBLIÉ (CMS admin). Alimente l'Accueil : bloc `hero` (override du titre/sous-titre)
 * + `announcement` (bannières d'annonces). Ne throw jamais côté page : le BFF renvoie une liste vide
 * si la table n'est pas encore provisionnée. `content` est optionnel — la page garde ses valeurs par
 * défaut i18n si le CMS est vide.
 */
export function useSiteContent() {
  return useQuery({
    queryKey: ["public", "content"],
    queryFn: ({ signal }) => api.get<SiteContentList>("/content", { signal }),
    staleTime: 120_000,
    retry: false,
  });
}

/** Extrait, parmi une liste de contenu, la première entrée publiée d'un type donné (par `order`). */
export function pickContent(
  list: SiteContentList | undefined,
  type: SiteContent["type"],
): SiteContent | undefined {
  return list?.items.find((c) => c.type === type);
}

/** Toutes les entrées publiées d'un type donné, déjà triées par `order` côté BFF. */
export function filterContent(
  list: SiteContentList | undefined,
  type: SiteContent["type"],
): SiteContent[] {
  return (list?.items ?? []).filter((c) => c.type === type);
}
